<template>
  <Collapse
    class="green-operation__electricity"
    :title="$t('tenancy.greenOperation.assessments.electricityData')"
  >
    <div
      slot="content"
      class="row"
    >
      <div class="small-12">
        <span class="font-bold">
          {{ $t('tenancy.greenOperation.assessments.electricityTableTitle') }}
        </span>
      </div>
      <template v-if="isTenant && sortedSubmittedDocs.length === 0">
        {{ $t('emptyValue') }}
      </template>
      <div
        v-else-if="sortedSubmittedDocs.length > 0"
        class="small-12"
      >
        <HKLTable
          :dataSource="sortedSubmittedDocs"
          :columns="columns"
        />
      </div>
    </div>
  </Collapse>
</template>

<script>
import { getFloorIndex } from '@/api/buildings/request'
import Collapse from '@/controls/Collapse'
import HKLText from '@/controls/HKLText.vue'
import HKLGrayHint from '@/controls/HKLGrayHint.vue'
import HKLFlex from '@/controls/Layout/HKLFlex.vue'
import HKLRenderList from '@/controls/Table/HKLRenderList.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import HKLDownloadLink from '@/controls/Upload/HKLDownloadLink.vue'
import { useAttachments } from '@/controls/Upload/hook'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { numberIterator, sortFloors } from '@/utils/sortingHelper'
import lodash from 'lodash'
import { computed, reactive, toRefs, unref } from 'vue-demi'
import { useCurrentTenancyBuilding } from '../../hook'
import { GREEN_OPERATION_ASSESSMENT_TYPE } from '../consts'
import { useGreenOperationField } from '../hook'
import { useSelectedLetterAssessments } from './hook'
import HKLDescriptionPencilInput from '@/controls/Input/HKLDescriptionPencilInput.vue'
import { updateDescription } from '@/api/docs/request'
import { useStoreGetters } from '@/store/index'
import { parseSubmitDate } from '../utils'

export default {
  components: {
    HKLTable,
    Collapse,
  },
  setup () {
    const emptyValue = i18n.t('emptyValue')
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const sourceId = useGreenOperationField('id', '')
    const assessmentData = useSelectedLetterAssessments(GREEN_OPERATION_ASSESSMENT_TYPE.ELECTRICITY_BILL)
    const currentBuilding = useCurrentTenancyBuilding()
    const { data: documents, refresh: refreshDocuments } = useAttachments(sourceId)
    const formatFloors = (floor) => {
      const sortedFloorArray = sortFloors(floor, unref(currentBuilding).code)
      return sortedFloorArray.join(', ')
    }
    const formatMonth = (month) => {
      return dateUtil.formatByLongDate(month, 'MMM YY')
    }
    /** @type {import('types/vue-demi').reactive}  */
    const setupData = reactive({
      sourceId,
      documents,
      electricityAssessment: assessmentData,
      sortedSubmittedDocs: computed(() => {
        const rawMetadata = lodash.get(unref(assessmentData), 'submittedMetadata') || { documents: [] }
        const result = lodash.map(rawMetadata.documents, (item) => {
          const matchedDoc = unref(documents).find(doc => doc.id === item.id)
          const floorArrayList = (item.metaData.EBInfo || []).map(info => info.floor)
          const sortedFirstFloor = sortFloors(floorArrayList[0] || [], unref(currentBuilding).code)
          const monthArrayList = (item.metaData.EBInfo || []).map(info => info.month)
          const readingArrayList = (item.metaData.EBInfo || []).map(info => info.reading)
          const firstFloor = lodash.get(sortedFirstFloor, [0])
          const firstMonth = lodash.get(monthArrayList, [0])
          const firstReading = lodash.get(readingArrayList, [0])
          const submittedHistories = lodash.get(matchedDoc, 'metaData.submittedHistories') || []
          const firstSubmittedHistory = lodash.minBy(submittedHistories, 'createdAt')
          const lastSubmittedTime = lodash.max((submittedHistories).map(item => item.createdAt))
          const floorIndex = getFloorIndex(unref(currentBuilding).code, firstFloor)
          return {
            id: item.id,
            filename: lodash.get(item, 'filename') || i18n.t('emptyValue'),
            description: lodash.get(matchedDoc, 'description'),
            floorIndex,
            floorArrayList,
            monthArrayList,
            readingArrayList,
            firstReading,
            billDate: new Date(firstMonth),
            submittedDate: dateUtil.format(lastSubmittedTime, dateUtil.DD_MMM_YY_HH_MM),
            lastSubmittedTime: lastSubmittedTime ? new Date(lastSubmittedTime) : null,
            firstSubmittedOn: firstSubmittedHistory ? new Date(firstSubmittedHistory.createdAt) : null,
            firstSubmittedBy: firstSubmittedHistory ? firstSubmittedHistory.displayName : null,
          }
        })
        return lodash.orderBy(result, ['billDate', 'floorIndex', 'lastSubmittedTime', 'id'], ['asc', 'asc', 'asc', 'asc'])
      }),
      columns: computed(() => {
        return [
          {
            title: '#',
            dataIndex: 'number',
            disabledSorter: true,
            component: {
              name: HKLText,
              attrs: (_, __, index) => {
                return { text: index + 1 }
              },
            },
          },
          {
            title: i18n.t('table.header.month'),
            dataIndex: 'month',
            emptyValue: emptyValue,
            component: {
              name: HKLRenderList,
              attrs: (_, row) => {
                return { dataList: row.raw.monthArrayList, formatFnc: formatMonth }
              },
            },
            onSorter: ([_, order], __, list) => {
              return lodash.orderBy(list, ['raw.billDate'], order)
            },
          },
          {
            title: i18n.t('table.header.floor'),
            dataIndex: 'floorIndex',
            component: {
              name: HKLRenderList,
              attrs: (_, row) => {
                return { dataList: row.raw.floorArrayList, formatFnc: formatFloors }
              },
            },
            emptyValue: emptyValue,
            onSorter: ([_, order], __, list) => {
              return lodash.orderBy(list, ['raw.floorIndex'], order)
            },
          },

          {
            title: i18n.t('table.header.reading'),
            dataIndex: 'firstReading',
            emptyValue: emptyValue,
            component: {
              name: HKLRenderList,
              attrs: (_, row) => {
                return { dataList: row.raw.readingArrayList }
              },
            },
            onSorter: ([_, order], __, list) => {
              return lodash.orderBy(list, (item) => numberIterator(item.firstReading), order)
            },
          },
          {
            title: i18n.t('table.header.document'),
            dataIndex: 'filename',
            emptyValue: '',
            component: {
              name: HKLFlex,
              attrs: (_, row) => {
                const items = [
                  {
                    name: HKLDownloadLink,
                    attrs: {
                      doc: row.raw,
                    },
                  },
                ]
                row.raw.firstSubmittedBy && items.push({
                  name: HKLGrayHint,
                  attrs: {
                    fontSize: 11,
                    hint: i18n.t('submittedByOn', [row.raw.firstSubmittedBy, parseSubmitDate(row.raw.firstSubmittedOn)]),
                  },
                })
                return { items, direction: 'column' }
              },
            },
          },
          {
            title: i18n.t('table.header.description'),
            dataIndex: 'description',
            emptyValue: '',
            component: {
              name: HKLDescriptionPencilInput,
              attrs: (val) => ({
                value: val,
                defaultHint: '',
                canEdit: false,
              }),
              listeners: (_, { raw: file }) => ({
                async close (content) {
                  await updateDescription({ id: file.id, description: content })
                  await refreshDocuments()
                },
              }),
            },
          },
          {
            title: i18n.t('table.header.submittedDate'),
            dataIndex: 'submittedDate',
            emptyValue: emptyValue,
            onSorter: ([_, order], __, list) => {
              return lodash.orderBy(list, ['raw.lastSubmittedTime'], order)
            },
          },
        ]
      }),
    })
    return {
      isTenant,
      ...toRefs(setupData),
    }
  },
}
</script>

<style lang="scss" scoped>
@import './common.scss';

.green-operation__electricity {

  .hkl-table {
    margin-left: 0;
    margin-right: 0;
    word-break: keep-all;
    ::v-deep .ant-table-row {
      .description {
      max-width: 200px;
      }
      .filename {
        max-width: 150px;
      }
      .submitted-date {
        min-width: 140px;
      }
    }
  }
  ::v-deep .default-title {
    font-size: 14px;
  }
  &.collapse ::v-deep {
    .content {
      padding: 10px 15px;
    }
    .filter-bar {
      display: flex;
      &__label {
        line-height: 38px;
      }
      &__select {
        width: 80px;
      }
    }
  }
}
</style>
